import {
  SpeechInput,
  type SpeechInputHandle,
} from "@/components/ai-elements/speech-input";
import {
  ChatComposerFrame,
  ChatComposerToolbar,
} from "@/components/chat/chat-dialog-primitives";
import SlashCommandAutocomplete, {
  type SlashCommandAutocompleteRef,
} from "@/components/chat/slash-command-autocomplete";
import DialogCreatePrompt from "@/components/prompt/dialog-create-prompt";
import DialogSelectPrompt from "@/components/prompt/dialog-select-prompt";
import DialogViewPrompt from "@/components/prompt/dialog-view-prompt";
import PromptsPopover, {
  addPromptToHistory,
} from "@/components/prompt/prompts-popover";
import { LLMAdminOnlyBadge } from "@/components/team/llm-permissions/llm-admin-only-badge";
import {
  LLM_PROVIDER_LOGOS,
  type LLMProvider,
} from "@/components/team/llm-permissions/llm-provider-logos";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import hubspotIcon from "@/assets/integrations/hubspot.svg";
import seliarisIcon from "@/assets/integrations/seliaris.png";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useChatStream } from "@/contexts/chat-stream-context";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { useSeliarisIntegration } from "@/contexts/seliaris-integration-provider";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useSlashCommand } from "@/hooks/useSlashCommand";
import { useVisibleLLMGroups } from "@/hooks/useVisibleLLMGroups";
import { useScrollToBottom } from "@/hooks/useScrollToBottom";
import { useSpeechInput } from "@/hooks/useSpeechInput";
import {
  AttachmentIssue,
  useAttachmentChecks,
} from "@/hooks/useAttachmentChecks";
import useAccess from "@/hooks/useAccess";
import { useIsPeopleSearchOffered } from "@/hooks/useIsPeopleSearchOffered";
import { useIsContactRevealOffered } from "@/hooks/useIsContactRevealOffered";
import { useAutoTacticsSetting } from "@/hooks/useAutoTacticsSetting";
import { useClarificationSetting } from "@/hooks/useClarificationSetting";
import { useCompanyIdentifierCard } from "@/hooks/useCompanyIdentifierCard";
import { useLlmSelection } from "@/hooks/useLlmSelection";
import { useTranslation } from "@/hooks/useTranslation";
import { notifyPromptsChanged } from "@/lib/prompt-events";
import {
  buildPromptSnapshot,
  getPromptContent,
  getPromptId,
  getPromptStyle,
  getSupportedLang,
  recordPromptUsage,
} from "@/lib/prompt-utils";
import { ToggleAdminMode } from "@/components/admin/toggle-admin-mode";
import {
  ChatContextUsage,
  ChatContextUsageInfo,
} from "@/components/chat/chat-context-usage";
import {
  FILES_UPLOAD_SIZE_LIMIT_BYTES,
  FILES_UPLOAD_SIZE_LIMIT_LABEL,
} from "@/lib/conversation-files";
import { cn } from "@/lib/utils";
import {
  ChatForcedTool,
  ChatRequestMode,
  CompanyDTO,
  ConversationMessagePromptInfo,
  LLMSelection,
  PromptDTO,
} from "@shaarp/common";
import {
  AlertTriangleIcon,
  ArrowUpIcon,
  BrainCogIcon,
  Building2Icon,
  CameraIcon,
  CheckIcon,
  ChevronDownIcon,
  CircleStopIcon,
  GlobeIcon,
  InfoIcon,
  PaperclipIcon,
  PlusIcon,
  SparklesIcon,
  XIcon,
} from "lucide-react";
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import TextareaAutosize from "react-textarea-autosize";
import { useToast } from "@/components/ui/use-toast";

import AvatarCompany from "@/components/company/avatar-company";
import FileCard from "./file-card";
import VoiceRecordingBar from "@/components/ai-elements/voice-recording-bar";
import ChatCompanyContextPicker from "./chat-company-context-picker";
import {
  CHAT_REQUEST_MODE_CHIP_KEYS,
  CHAT_REQUEST_MODE_ICONS,
  CHAT_REQUEST_MODE_LABEL_KEYS,
  getChatRequestModePlaceholderKey,
} from "./chat-request-modes";
import {
  ChatCompanyIdentifierDropdown,
  type ChatCompanyIdentifierDropdownRef,
} from "./chat-company-identifier-dropdown";
import { MemoryMessageToSend } from "./chat-conversation";
import { ChatPlaceholderExamples } from "./chat-placeholder-examples";

export type ChatTextInputRef = {
  setInput: (value: string) => void;
  focus: () => void;
  /** Arms a request mode for the next message, or clears it with `null`. */
  setRequestMode: (mode: ChatRequestMode | null) => void;
};

type ChatTextInputProps = {
  /**
   * Hands the message off. Returns `false` when the send was refused and
   * nothing left the composer — the message, its attachments and the forced
   * tool are then kept where they are, rather than cleared over a send that
   * never happened. Anything else means the send was taken.
   */
  onMessageSent: (params: MemoryMessageToSend) => boolean | void;
  /**
   * Thread being written in. Empty for a chat that does not exist yet, which has
   * no history to fill and no earlier copy of a document to warn about.
   */
  conversationId?: string;
  /**
   * Text to put back in the composer, after a send the server refused. The
   * counter makes an identical text restore again on a second refusal.
   */
  draftToRestore?: { text: string; attempt: number } | null;
  /**
   * Called once the draft is in the field, so its owner can let it go: kept,
   * it would come back in every composer mounted after.
   */
  onDraftRestored?: () => void;
  streamInProgress?: boolean;
  triggerStopStream?: () => void;
  initialCompanyContext?: CompanyDTO | null;
  /** Context window of the last answered turn, for the admin-only debug ring. */
  contextUsage?: ChatContextUsageInfo;
  /**
   * Whether the empty field scrolls through example requests instead of the
   * plain placeholder. Reserved for the new-chat screen, where showing what can
   * be asked is worth the movement.
   */
  showPlaceholderExamples?: boolean;
  /**
   * Whether a message that is nothing but a company identifier — a SIRET, a
   * SIREN, a website, a LinkedIn page — shows a card of that company as it is
   * typed, and is never sent. The standalone chat's: a company's own chat
   * already knows which company it is about.
   */
  isCompanyIdentifierShortcutEnabled?: boolean;
  /** Told whenever the armed mode changes, from the composer or from outside. */
  onRequestModeChange?: (mode: ChatRequestMode | null) => void;
  /**
   * Whether the send button starts as send even though the composer mounts
   * streaming. Sending a new chat hands the composer over twice — to the
   * placeholder shown while the conversation is created, then to the
   * conversation's own — and each one mounts already streaming: the one taking
   * over from the composer the user pressed send in starts from send, so the
   * button morphs instead of appearing as stop; the one after starts from the
   * stop its predecessor already showed.
   */
  startsFromSend?: boolean;
};

const FILES_UPLOAD_LIMIT = 2;

const FILES_UPLOAD_EXTENSIONS_ACCEPTED = [
  "txt",
  "pdf",
  "csv",
  "xls",
  "xlsx",
  "doc",
  "docx",
  "odt",
  "odp",
  "ods",
  "ppt",
  "pptx",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  // SVG excluded: can contain embedded scripts (XSS risk)
];

// Whitelist for MIME-based acceptance of clipboard images, which often arrive
// as `File` instances without an extension in their name (e.g. macOS/Windows
// screenshots pasted with Ctrl+V).
const FILES_UPLOAD_IMAGE_MIME_TYPES_ACCEPTED = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

const MIME_TO_EXTENSION: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

/**
 * Drops the blank lines before a pasted text when nothing precedes the
 * selection, and the whitespace after it when nothing follows: against text
 * already in the field, they are what keeps the paste apart from it. Keeps the
 * indentation of its first line and every line break inside it.
 */
const trimPastedText = (text: string, field: HTMLTextAreaElement): string => {
  const head = field.selectionStart === 0 ? text.replace(/^\s*\n/, "") : text;
  return field.selectionEnd === field.value.length ? head.trimEnd() : head;
};

/** Label shown in the inline chip once a tool has been forced. */
const FORCED_TOOL_ACTIVE_LABEL_KEYS: Record<ChatForcedTool, string> = {
  webSearch: "chat.input.tools.webSearchActive",
  hubspotCompanies: "chat.input.tools.hubspotCompaniesActive",
  seliarisCompanies: "chat.input.tools.seliarisCompaniesActive",
};

/** Logo shown next to a forced tool, for the tools that have one. */
const FORCED_TOOL_ICONS: Partial<Record<ChatForcedTool, string>> = {
  hubspotCompanies: hubspotIcon,
  seliarisCompanies: seliarisIcon,
};

function ensureFileExtension(file: File): File {
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension && FILES_UPLOAD_EXTENSIONS_ACCEPTED.includes(extension)) {
    return file;
  }
  const mappedExtension = MIME_TO_EXTENSION[file.type];
  if (!mappedExtension) return file;
  const baseName = file.name && file.name !== "image" ? file.name : "pasted-image";
  return new File([file], `${baseName}-${Date.now()}.${mappedExtension}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/**
 * One "how the assistant works" switch: a label, an explanation folded behind
 * an info bubble, and the switch itself.
 *
 * Shared rather than written twice: the rows are identical down to the
 * accessibility wiring, and the only thing a reader should have to compare
 * between two of them is their wording.
 */
type BehaviourSwitchRowProps = {
  readonly variant: "popover" | "sheet";
  readonly label: string;
  readonly hint: string;
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
};

function BehaviourSwitchRow({
  variant,
  label,
  hint,
  checked,
  onCheckedChange,
}: BehaviourSwitchRowProps): React.JSX.Element {
  const hintId = useId();
  // Driven by us rather than by Radix alone so the icon opens the tooltip on
  // tap too: the same menu is the mobile sheet, where a hover never fires.
  const [isHintOpen, setIsHintOpen] = useState(false);

  return (
    <div
      className={cn(
        "flex items-center gap-3",
        variant === "sheet" ? "px-4 py-3" : "px-3 py-2",
      )}
    >
      <div className="flex items-center flex-1 gap-1.5 min-w-0">
        <span className={variant === "sheet" ? "text-base" : "text-sm"}>
          {label}
        </span>
        {/* Folded behind an info bubble: the popover sizes to its content, and
            spelled out the sentence stretched the menu across the window. */}
        <Tooltip
          delayDuration={0}
          open={isHintOpen}
          onOpenChange={setIsHintOpen}
        >
          <TooltipTrigger asChild>
            {/* Hidden from screen readers, which get the same sentence from the
                switch's description below. */}
            <span
              aria-hidden
              className="text-muted-foreground hover:text-foreground cursor-help"
              onClick={() => setIsHintOpen(true)}
            >
              <InfoIcon className={variant === "sheet" ? "size-4" : "size-3.5"} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px]">
            <p className="text-xs leading-snug">{hint}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      {/* Kept in the DOM rather than read off the tooltip, which only exists
          while it is open. */}
      <span id={hintId} className="sr-only">
        {hint}
      </span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
        aria-describedby={hintId}
      />
    </div>
  );
}

/** Side of the round send button — `size="icon"`, i.e. Tailwind's `size-9`. */
const SEND_BUTTON_SIZE_PX = 36;
/** The stop button's corners — Tailwind's `rounded-md`. */
const STOP_BUTTON_RADIUS_PX = 6;

/**
 * The send button's size and corners: round as send, a labelled pill as stop.
 * The pill's width is its label's, in pixels once measured: left at `auto`, the
 * way back to send would measure it after the label has already left, and
 * shrink from nothing instead of from the pill.
 */
function getSendButtonShape(
  isStop: boolean,
  isMobile: boolean | undefined,
  stopLabelWidth: number | undefined
): { width: number | "auto"; borderRadius: number } {
  return {
    width: isStop && !isMobile ? (stopLabelWidth ?? "auto") : SEND_BUTTON_SIZE_PX,
    borderRadius: isStop ? STOP_BUTTON_RADIUS_PX : SEND_BUTTON_SIZE_PX / 2,
  };
}

export const ChatTextInput = forwardRef<ChatTextInputRef, ChatTextInputProps>(
  function ChatTextInput(
    {
      onMessageSent,
      conversationId = "",
      draftToRestore,
      onDraftRestored,
      streamInProgress = false,
      triggerStopStream = async () => {},
      initialCompanyContext = null,
      contextUsage,
      showPlaceholderExamples = false,
      isCompanyIdentifierShortcutEnabled = false,
      onRequestModeChange,
      startsFromSend = false,
    },
    ref
  ) {
    const shouldReduceMotion = useReducedMotion();
    // Frozen at mount: the shape the button animates from, see `startsFromSend`.
    const [mountedAsStop] = useState(
      () => streamInProgress && !startsFromSend
    );
    const [stopLabelWidth, setStopLabelWidth] = useState<number>();
    const measureStopLabel = useCallback((label: HTMLSpanElement | null) => {
      if (label) setStopLabelWidth(label.offsetWidth);
    }, []);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const forcedToolPrefixRef = useRef<HTMLButtonElement>(null);
    const requestModePrefixRef = useRef<HTMLButtonElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const slashCommandRef = useRef<SlashCommandAutocompleteRef>(null);
    const identifierDropdownRef =
      useRef<ChatCompanyIdentifierDropdownRef>(null);
    // Everything the composer is made of, dropdown included: a click landing
    // anywhere in here is the user still at work, and puts nothing away.
    const composerRef = useRef<HTMLDivElement>(null);
    const speechInputRef = useRef<SpeechInputHandle>(null);
    // Input value captured when recording starts, restored if the user discards.
    const inputSnapshotRef = useRef<string>("");
    // How the active recording is being ended, so the listening-stopped handler
    // knows whether to discard the transcription or keep it.
    const recordingActionRef = useRef<"none" | "cancel" | "confirm">("none");
    const { isMobile } = useDeviceFormat();

    useImperativeHandle(ref, () => ({
      setInput: (value: string) => {
        setUserInput(value);
      },
      focus: () => {
        textareaRef.current?.focus();
      },
      setRequestMode: (mode: ChatRequestMode | null) => {
        changeRequestMode(mode);
      },
    }));
    const { toast } = useToast();
    const { isAdminMode } = useAdminMode();
    const { hasAccess } = useAccess();
    const { hubspotIntegration } = useHubSpotIntegration();
    const { seliarisIntegration } = useSeliarisIntegration();
    // Unconnected teams never see the entry at all — the API drops the CRM
    // tools for them, so it would advertise something that cannot happen. A
    // connected-but-rejected credential stays listed and disabled instead,
    // because there the user has an action to take.
    const isHubspotConnected = hubspotIntegration?.isActive === true;
    const isHubspotAvailable =
      isHubspotConnected && hubspotIntegration?.isAvailable === true;
    const isSeliarisConnected = seliarisIntegration?.isActive === true;
    // `isAvailable` also drops on a read-only account, which only refuses
    // *writes* — this tool reads. A rejected key is the one thing that stops
    // the lookup, and it is what read-only leaves behind when excluded.
    const isSeliarisAvailable =
      isSeliarisConnected &&
      (seliarisIntegration?.isAvailable === true ||
        seliarisIntegration?.isReadOnly === true);
    const visibleLLMGroups = useVisibleLLMGroups();
    const { t, i18n } = useTranslation();
    const { stopStreamBeforeSend } = useChatStream();
    const { llmSelection, setLlmSelection: setLLMSelection } =
      useLlmSelection();
    // Kept from one message to the next, like the model: turning the questions
    // off is a way of working, not a property of a single message.
    const {
      isEnabled: isClarificationEnabled,
      setIsEnabled: setIsClarificationEnabled,
    } = useClarificationSetting();
    // Same reasoning, one switch down: whether the assistant may reach for a
    // tactic on its own outlives the message it was flipped on.
    const {
      isEnabled: isAutoTacticsEnabled,
      setIsEnabled: setIsAutoTacticsEnabled,
    } = useAutoTacticsSetting();

    const [userInput, setUserInput] = useState<string>("");

    // Put back what the user had typed when the server refused the send. Keyed
    // on the attempt counter rather than the text, so refusing the same message
    // twice restores it both times.
    const onDraftRestoredRef = useRef(onDraftRestored);
    onDraftRestoredRef.current = onDraftRestored;
    useEffect(() => {
      if (draftToRestore?.text) {
        setUserInput(draftToRestore.text);
        onDraftRestoredRef.current?.();
      }
    }, [draftToRestore?.attempt, draftToRestore?.text]);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    // Checked as soon as they are attached, in one step: size, room left in the
    // conversation, and whether the document was already sent. This is the only
    // moment the user can still act on any of the three.
    const attachmentChecks = useAttachmentChecks({
      files: uploadedFiles,
      llmSelection: llmSelection ?? "auto",
      conversationId,
    });
    const { blockedFiles, duplicateFiles } = attachmentChecks;
    // A duplicate is a warning, so the user can dismiss it and send anyway. Keyed
    // on the files themselves: attaching another document raises the question
    // again for that one.
    const [dismissedDuplicates, setDismissedDuplicates] = useState<Set<File>>(
      new Set()
    );
    const pendingDuplicates = duplicateFiles.filter(
      (file) => !dismissedDuplicates.has(file)
    );
    const [isRecording, setIsRecording] = useState(false);
    // False once the realtime channel dies mid-dictation: capture continues but
    // the transcript only lands when the user stops.
    const [isLiveTranscript, setIsLiveTranscript] = useState(true);
    const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
      null
    );
    const [fileObjectUrls, setFileObjectUrls] = useState<
      Map<File, string>
    >(new Map());

    const getFileObjectUrl = useCallback(
      (file: File): string => {
        const existing = fileObjectUrls.get(file);
        if (existing) return existing;
        const url = URL.createObjectURL(file);
        setFileObjectUrls((prev) => new Map(prev).set(file, url));
        return url;
      },
      [fileObjectUrls]
    );
    const {
      isActive: isSlashActive,
      promptGroups: slashGroups,
      totalCount: slashTotalCount,
      dismiss: dismissSlashCommand,
    } = useSlashCommand(userInput);

    const filePreviewUrls = useMemo(
      () =>
        uploadedFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
      [uploadedFiles]
    );

    useEffect(() => {
      return () => {
        filePreviewUrls.forEach(({ url }) => URL.revokeObjectURL(url));
      };
    }, [filePreviewUrls]);

    const [dialogSelectPrompt, setDialogSelectPrompt] =
      useState<boolean>(false);
    const [dialogCreatePrompt, setDialogCreatePrompt] =
      useState<boolean>(false);
    // Tool the user forced for the next message, if any. Single-select: picking
    // one in the tools menu replaces the previous choice.
    const [forcedTool, setForcedTool] = useState<ChatForcedTool | null>(null);
    // Request mode armed for the next message, if any. Exclusive with the
    // forced tool: the composer offers one choice per message, as the API
    // keeps only the tool when both arrive.
    const [requestMode, setRequestMode] = useState<ChatRequestMode | null>(
      null
    );
    const changeRequestMode = (mode: ChatRequestMode | null): void => {
      setRequestMode(mode);
      onRequestModeChange?.(mode);
      if (mode) setForcedTool(null);
    };
    const changeForcedTool = (tool: ChatForcedTool | null): void => {
      setForcedTool(tool);
      if (tool) changeRequestMode(null);
    };
    // The contact mode is only offered to a team that can get a contact card:
    // one armed before the team lost it (a team switch, a flag cut) must not
    // be sent with the next message.
    const isContactRevealOffered = useIsContactRevealOffered();
    useEffect(() => {
      if (isContactRevealOffered || requestMode !== "contact") return;
      setRequestMode(null);
      onRequestModeChange?.(null);
    }, [isContactRevealOffered, requestMode, onRequestModeChange]);
    // Whether « Prospection » also lists people (#668): the same answer as the
    // flag the conversation sends, so the placeholder never promises people
    // the server will not search — not on a phone, not about one company.
    const isPeopleSearchOffered = useIsPeopleSearchOffered({
      isCompanyTargeted: !!initialCompanyContext,
    });
    // Width of the inline forced-tool prefix overlay, used to indent the first
    // line of the textarea so typed text flows to its right (Dia-style).
    const [forcedToolPrefixWidth, setForcedToolPrefixWidth] =
      useState<number>(0);
    // Same, for the request mode's prefix.
    const [requestModePrefixWidth, setRequestModePrefixWidth] =
      useState<number>(0);
    // Company the conversation was created with (enriched at creation). Shown as
    // a locked chip the user cannot remove, so every message keeps targeting it.
    // The API already knows it via the conversation, so it is never re-sent.
    const lockedCompany = initialCompanyContext;
    // Extra company attached from the input on top of the locked one. Sticky: it
    // persists across sends until removed, and is the only company sent per
    // message (the API combines it with the conversation's locked company).
    const [companyContext, setCompanyContext] = useState<CompanyDTO | null>(
      null
    );
    const [companyPickerOpen, setCompanyPickerOpen] = useState<boolean>(false);
    const [toolsMenuOpen, setToolsMenuOpen] = useState<boolean>(false);
    const [modelSheetOpen, setModelSheetOpen] = useState<boolean>(false);
    const [dialogViewPrompt, setDialogViewPrompt] = useState<boolean>(false);
    const [selectedPrompt, setSelectedPrompt] = useState<PromptDTO | null>(
      null
    );
    // The card leads to the company page, which the professional licence
    // gates: without it, the message goes to the chat as it always has.
    const canOpenCompanyPage = hasAccess({
      requiredLicences: ["professional"],
      licenceActiveState: true,
    });
    // The company an identifier typed alone designates, shown above the
    // composer as soon as the field settles on it, and in place of sending the
    // message should the user press Enter first.
    const identifierCard = useCompanyIdentifierCard(
      userInput,
      {
        isEnabled: isCompanyIdentifierShortcutEnabled,
        canOpenCompanyPage,
        hasForcedTool: forcedTool !== null,
        hasAttachments: uploadedFiles.length > 0,
        hasTactic: selectedPrompt !== null,
        // Admin-only, and only sent while admin mode lasts: a residual
        // selection left by an expired admin mode says nothing about this
        // message.
        hasCompanyContext: isAdminMode && companyContext !== null,
      },
      () => sendNewMessage(undefined, { skipCompanyIdentifier: true })
    );

    const {
      speechLanguage,
      handlePermissionDenied,
      handleError,
      handleConnectionLost,
    } = useSpeechInput();

    /**
     * Live dictation rewrites the composer on every delta, so it is rebuilt
     * from the snapshot taken when recording started rather than appended to —
     * appending would duplicate the text as the transcript grows.
     */
    // Follow the transcript as it is written past the composer's max height.
    useScrollToBottom(textareaRef, userInput, isRecording);

    const handleTranscript = useCallback((text: string) => {
      const snapshot = inputSnapshotRef.current;
      setUserInput(snapshot.trim() ? `${snapshot.trimEnd()} ${text}` : text);
    }, []);

    useEffect(() => {
      // Skip validation if auto mode is selected - the API will resolve to team default
      if (llmSelection === "auto" || visibleLLMGroups.length === 0) {
        return;
      }

      const isValid = visibleLLMGroups
        .flatMap((group) => group.models)
        .some((model) => model.id === llmSelection && model.available);

      // Selected model is missing, disabled, or no longer accessible
      // (e.g. admin-only after admin mode timed out): hand back to auto,
      // which the API resolves to the team's default LLM.
      if (!isValid) {
        setLLMSelection("auto");
      }
    }, [visibleLLMGroups, llmSelection, setLLMSelection]);

    // Drop a CRM force if the integration goes away or breaks while the user is
    // composing, otherwise the chip promises a lookup that will not happen.
    useEffect(() => {
      if (forcedTool === "hubspotCompanies" && !isHubspotAvailable) {
        setForcedTool(null);
      }
      if (forcedTool === "seliarisCompanies" && !isSeliarisAvailable) {
        setForcedTool(null);
      }
    }, [forcedTool, isHubspotAvailable, isSeliarisAvailable]);

    // Measure the inline forced-tool prefix so the textarea's first line is
    // indented exactly past it. Re-measured when the tool or the locale changes
    // (label width is locale-dependent). +8px keeps typed text off the label.
    useLayoutEffect(() => {
      if (!forcedTool || !forcedToolPrefixRef.current) {
        setForcedToolPrefixWidth(0);
        return;
      }
      setForcedToolPrefixWidth(forcedToolPrefixRef.current.offsetWidth + 8);
    }, [forcedTool, i18n.language]);

    useLayoutEffect(() => {
      if (!requestMode || !requestModePrefixRef.current) {
        setRequestModePrefixWidth(0);
        return;
      }
      setRequestModePrefixWidth(requestModePrefixRef.current.offsetWidth + 8);
    }, [requestMode, i18n.language]);

    const getModelLogo = (groupId: string) =>
      LLM_PROVIDER_LOGOS[groupId as LLMProvider];

    const getSelectedModelLabel = () => {
      if (llmSelection === "auto") {
        return (
          <div className="flex gap-2 items-center min-w-0">
            <SparklesIcon className="w-3 h-3 shrink-0" />
            <span className="truncate">{t("chat.input.models.auto.short")}</span>
          </div>
        );
      }
      const model = visibleLLMGroups
        .flatMap((g) => g.models)
        .find((m) => m.id === llmSelection);
      const modelLogo = model && getModelLogo(model.groupId);
      return (
        <div className="flex gap-2 items-center min-w-0">
          {modelLogo ? (
            <img
              src={modelLogo}
              alt={model.groupId}
              className="w-3 h-3 shrink-0"
            />
          ) : (
            <BrainCogIcon className="w-3 h-3 shrink-0" />
          )}
          <span className="truncate">
            {model?.name || t("chat.input.models.select")}
          </span>
        </div>
      );
    };

    const handleSelectModel = (value: LLMSelection) => {
      setLLMSelection(value);
      setModelSheetOpen(false);
    };

    const sendNewMessage = (
      newMessage?: {
        message: string;
        files: File[];
        prompt?: ConversationMessagePromptInfo;
      },
      {
        skipCompanyIdentifier = false,
      }: {
        /** Sent from the identifier card: the user chose the chat. */
        skipCompanyIdentifier?: boolean;
      } = {}
    ) => {
      if (!newMessage && !userInput && !selectedPrompt) return;
      // An attachment the model cannot read makes the whole send pointless: the
      // server would refuse it after the file had already been uploaded. Guarded
      // here and not only on the button, so Enter cannot slip past it. A
      // duplicate never blocks — it is a waste of context, not a mistake.
      //
      // A check still running blocks too, which is the whole point of showing it:
      // sending during it would be sending before the verdict, i.e. exactly the
      // dead end the check exists to prevent. The spinner on the card is what
      // says why the button is inert.
      if (blockedFiles.length > 0 || attachmentChecks.isChecking) return;

      // Before the stream is stopped: opening a card needs no model, and must
      // never cut short an answer being written.
      if (!newMessage && !skipCompanyIdentifier && identifierCard.open()) {
        return;
      }

      const llmId: LLMSelection = llmSelection ?? "auto";

      // The company-context feature is admin-only; never leak a residual
      // selection if admin mode expired while the user was typing.
      const effectiveCompanyContext = isAdminMode ? companyContext : null;

      void stopStreamBeforeSend().then(() => {
        // Whether the send was taken. The composer is cleared optimistically —
        // the upload runs behind it — so a refusal has to be told apart here
        // or it costs the user everything they had staged.
        let wasTaken: boolean | void;
        if (newMessage) {
          wasTaken = onMessageSent({
            llmId,
            text: newMessage.message,
            filesAttached: newMessage.files,
            forcedTool,
            requestMode,
            companyContext: effectiveCompanyContext,
            prompt: newMessage.prompt,
          });
        } else {
          const lang = getSupportedLang(i18n.language);
          const promptContent = selectedPrompt
            ? getPromptContent(selectedPrompt, lang)
            : "";
          const finalMessage =
            promptContent && userInput
              ? `${promptContent}\n\n${userInput}`
              : promptContent || userInput;

          const prompt = selectedPrompt
            ? buildPromptSnapshot(selectedPrompt, userInput, lang)
            : undefined;

          wasTaken = onMessageSent({
            llmId,
            text: finalMessage,
            filesAttached: uploadedFiles,
            forcedTool,
            requestMode,
            companyContext: effectiveCompanyContext,
            prompt,
          });
        }

        if (wasTaken === false) return;

        setUserInput("");
        fileObjectUrls.forEach((url) => URL.revokeObjectURL(url));
        setFileObjectUrls(new Map());
        setUploadedFiles([]);
        setDismissedDuplicates(new Set());
        setForcedTool(null);
        // For one message, like the forced tool: a mode left armed would turn
        // "écris-lui un email" into a search.
        changeRequestMode(null);
        setSelectedPrompt(null);
      });
    };

    /** The card's way back to the chat: the message, sent as it would have been. */
    const askChatInstead = (): void => {
      identifierCard.close();
      sendNewMessage(undefined, { skipCompanyIdentifier: true });
    };

    /** The company page opened in a tab of its own: the composer starts over. */
    const handleIdentifierCardOpened = (): void => {
      identifierCard.close();
      setUserInput("");
    };

    const handleRecordingChange = (isRecordingNow: boolean) => {
      setIsRecording(isRecordingNow);
      if (isRecordingNow) {
        inputSnapshotRef.current = userInput;
        return;
      }
      // Recording stopped: discard the live transcription when cancelled.
      if (recordingActionRef.current === "cancel") {
        setUserInput(inputSnapshotRef.current);
      }
      recordingActionRef.current = "none";
    };

    const handleCancelRecording = () => {
      recordingActionRef.current = "cancel";
      speechInputRef.current?.cancel();
    };

    // Stop recording but keep the transcription so the user can review and edit
    // it before sending, instead of dispatching the message right away.
    const handleConfirmRecording = () => {
      recordingActionRef.current = "confirm";
      speechInputRef.current?.stop();
      setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const handlePromptSelection = (prompt: PromptDTO) => {
      const lang = getSupportedLang(i18n.language);
      addPromptToHistory(prompt, undefined, lang);
      recordPromptUsage(getPromptId(prompt));
      setDialogSelectPrompt(false);
      setSelectedPrompt(prompt);
      setUserInput("");
      setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const removeSelectedPrompt = () => {
      setSelectedPrompt(null);
      textareaRef.current?.focus();
    };

    const processFilesUpload = (files: File[]) => {
      let alertMessageTooLarge = false;
      let alertMessageUnsupportedFormat = false;
      const uploadedFilesCount = uploadedFiles.length;

      let newFiles = files
        .map((file) => ensureFileExtension(file))
        .filter((file) => {
          const extension = file.name.split(".").pop()?.toLowerCase();
          const hasValidExtension =
            !!extension && FILES_UPLOAD_EXTENSIONS_ACCEPTED.includes(extension);
          const hasValidImageMime =
            FILES_UPLOAD_IMAGE_MIME_TYPES_ACCEPTED.includes(file.type);

          if (file.size > FILES_UPLOAD_SIZE_LIMIT_BYTES) {
            alertMessageTooLarge = true;
            return false;
          }

          if (!hasValidExtension && !hasValidImageMime) {
            alertMessageUnsupportedFormat = true;
            return false;
          }

          return true;
        });

      if (alertMessageTooLarge) {
        toast({
          variant: "warning",
          title: t("chat.input.files.tooLarge.title"),
          description: t("chat.input.files.tooLarge.description", {
            size: FILES_UPLOAD_SIZE_LIMIT_LABEL,
          }),
        });
      }

      if (alertMessageUnsupportedFormat) {
        toast({
          variant: "warning",
          title: t("chat.input.files.unsupportedFormat.title"),
          description: t("chat.input.files.unsupportedFormat.description", {
            formats: FILES_UPLOAD_EXTENSIONS_ACCEPTED.map(
              (ext) => `.${ext}`
            ).join(", "),
          }),
        });
      }

      if (uploadedFilesCount + newFiles.length > FILES_UPLOAD_LIMIT) {
        newFiles = newFiles.slice(0, FILES_UPLOAD_LIMIT - uploadedFilesCount);
        toast({
          variant: "warning",
          title: t("chat.input.files.tooMany.title"),
          description: t("chat.input.files.tooMany.description", {
            count: FILES_UPLOAD_LIMIT,
          }),
        });
      }

      setUploadedFiles([...uploadedFiles, ...newFiles]);
      if (newFiles.length > 0) {
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFilesUpload(Array.from(e.target.files));
        e.target.value = "";
      }
    };

    const removeFile = (index: number) => {
      const file = uploadedFiles[index];
      const url = fileObjectUrls.get(file);
      if (url) {
        URL.revokeObjectURL(url);
        setFileObjectUrls((prev) => {
          const next = new Map(prev);
          next.delete(file);
          return next;
        });
      }
      setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    /**
     * Drops several attachments at once, for the "remove them" action of the
     * notice. Goes through the same object-URL cleanup as removing one by hand.
     */
    const removeFiles = (filesToRemove: File[]) => {
      const doomed = new Set(filesToRemove);
      setFileObjectUrls((prev) => {
        const next = new Map(prev);
        doomed.forEach((file) => {
          const url = next.get(file);
          if (url) URL.revokeObjectURL(url);
          next.delete(file);
        });
        return next;
      });
      setUploadedFiles((previous) =>
        previous.filter((file) => !doomed.has(file))
      );
    };

    /** The attached names as they read inside a sentence. */
    const fileNames = (files: File[]) =>
      files.map((file) => file.name).join(", ");

    /** One short line saying what is wrong with an attachment. */
    const describeIssue = (issue: AttachmentIssue): string =>
      issue.kind === "duplicate"
        ? t("chat.input.files.checks.issue.duplicate", {
            date: new Date(issue.alreadySentAt).toLocaleDateString(
              i18n.language
            ),
          })
        : t(`chat.input.files.checks.issue.${issue.kind}`);

    // One notice per reason rather than one for all the refusals: "too big for
    // this model" and "no room left in this conversation" call for different
    // things, and a single sentence covering both would be true of neither.
    const blockedByReason = blockedFiles.reduce((groups, file) => {
      const verdict = attachmentChecks.verdicts.get(file);
      if (verdict?.status !== "blocked") return groups;
      const existing = groups.get(verdict.issue.kind) ?? [];
      groups.set(verdict.issue.kind, [...existing, file]);
      return groups;
    }, new Map<AttachmentIssue["kind"], File[]>());

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer.files) {
        processFilesUpload(Array.from(e.dataTransfer.files));
      }
    };

    const RequestModeIcon = requestMode
      ? CHAT_REQUEST_MODE_ICONS[requestMode]
      : null;

    // The examples take over the placeholder slot only when nothing else claims
    // it: a forced tool, a tactic and a recording all have their own hint to
    // give.
    const isShowingExamples =
      showPlaceholderExamples &&
      !userInput &&
      !selectedPrompt &&
      !isRecording &&
      !streamInProgress &&
      !forcedTool &&
      !requestMode;

    // Emptied while the rotating examples own the slot: they are an overlay, and
    // a native placeholder underneath would show through them.
    const placeholder = isShowingExamples
      ? ""
      : isRecording && !userInput
        ? t("chat.input.speech.listening")
        : selectedPrompt
          ? t("chat.input.slashCommand.addContext")
          : requestMode
            ? t(
                getChatRequestModePlaceholderKey(
                  requestMode,
                  isPeopleSearchOffered
                )
              )
            : t("chat.input.placeholder");

    const selectedPromptColors = selectedPrompt
      ? getPromptStyle(selectedPrompt).colors
      : null;
    const selectedPromptClassName = selectedPromptColors
      ? `${selectedPromptColors.border} ${selectedPromptColors.shadow} -translate-y-0.5`
      : "";

    const renderCompanyChip = (company: CompanyDTO, removable: boolean) => (
      <div
        key={company.id}
        className="group relative inline-flex items-center gap-2.5 py-2 pr-4 pl-2 rounded-xl border bg-muted/50"
      >
        <AvatarCompany
          company={company}
          className="rounded-lg border size-9 bg-background"
        />
        <div className="flex flex-col min-w-0">
          <span className="max-w-[12rem] text-sm font-medium leading-tight truncate">
            {company.name}
          </span>
          {company.domain && (
            <span className="max-w-[12rem] text-xs leading-tight truncate text-muted-foreground">
              {company.domain}
            </span>
          )}
        </div>
        {removable && (
          <button
            type="button"
            onClick={() => setCompanyContext(null)}
            className="flex absolute -top-1.5 -right-1.5 z-10 justify-center items-center rounded-full border opacity-0 transition-opacity size-5 bg-secondary text-secondary-foreground group-hover:opacity-100 touch:opacity-100 hover:bg-destructive hover:text-destructive-foreground"
            aria-label={t("chat.input.companyContext.remove")}
          >
            <XIcon className="size-3" />
          </button>
        )}
      </div>
    );

    const renderToolsMenuItems = (variant: "popover" | "sheet") => {
      const itemClassName = cn(
        "flex items-center gap-3 w-full text-left transition-colors hover:bg-muted active:bg-muted",
        variant === "sheet"
          ? "px-4 py-3.5 rounded-xl text-base"
          : "px-3 py-2 rounded-md text-sm",
      );
      const iconClassName = cn(
        "shrink-0",
        variant === "sheet" ? "size-5" : "size-4",
      );
      const sectionLabelClassName = cn(
        "px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground",
        variant === "sheet" && "px-4 text-sm",
      );

      /** Toggles a forceable tool; picking the active one clears the force. */
      const renderForcedToolItem = (
        tool: ChatForcedTool,
        icon: ReactNode,
        label: string,
        disabledReason?: string,
      ) => {
        const isSelected = forcedTool === tool;
        const isDisabled = !!disabledReason;

        const item = (
          <button
            type="button"
            // `disabled` sets pointer-events-none, which would swallow the
            // hover the tooltip needs — hence aria-disabled + a no-op handler.
            aria-disabled={isDisabled}
            className={cn(
              itemClassName,
              isDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent active:bg-transparent",
            )}
            onClick={() => {
              if (isDisabled) return;
              changeForcedTool(isSelected ? null : tool);
              setToolsMenuOpen(false);
            }}
          >
            {icon}
            <span
              className={cn(
                "min-w-0",
                variant === "sheet" ? "flex-1" : "whitespace-nowrap",
              )}
            >
              <span className="block">{label}</span>
              {/* The sheet is touch-only, where a hover tooltip never fires, so
                  the reason has to be inline. */}
              {isDisabled && variant === "sheet" && (
                <span className="block text-xs leading-snug text-muted-foreground">
                  {disabledReason}
                </span>
              )}
            </span>
            {isDisabled && (
              <AlertTriangleIcon
                className={cn(iconClassName, "ml-auto text-red-600")}
              />
            )}
            {isSelected && (
              <CheckIcon className={cn(iconClassName, "text-primary")} />
            )}
          </button>
        );

        // The sheet already carries the reason inline (see above).
        if (!isDisabled || variant === "sheet") return item;

        return (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{item}</TooltipTrigger>
            <TooltipContent side="right" className="max-w-[240px]">
              {disabledReason}
            </TooltipContent>
          </Tooltip>
        );
      };

      return (
        <div className="flex flex-col">
          <button
            className={itemClassName}
            onClick={() => {
              setToolsMenuOpen(false);
              fileInputRef.current?.click();
            }}
          >
            <PaperclipIcon className={iconClassName} />
            <span>{t("chat.input.tools.uploadFiles")}</span>
          </button>
          {isMobile && (
            <button
              className={itemClassName}
              onClick={() => {
                setToolsMenuOpen(false);
                cameraInputRef.current?.click();
              }}
            >
              <CameraIcon className={iconClassName} />
              <span className="whitespace-nowrap">
                {t("chat.input.tools.takePhoto")}
              </span>
            </button>
          )}
          {renderForcedToolItem(
            "webSearch",
            <GlobeIcon className={iconClassName} />,
            t("chat.input.tools.webSearch"),
          )}
          {(isHubspotConnected || isSeliarisConnected) && (
            <>
              <div className={sectionLabelClassName}>
                {t("chat.input.tools.integrations")}
              </div>
              {isHubspotConnected &&
                renderForcedToolItem(
                  "hubspotCompanies",
                  <img
                    src={hubspotIcon}
                    alt=""
                    aria-hidden
                    className={cn(iconClassName, "object-contain")}
                  />,
                  t("chat.input.tools.hubspotCompanies"),
                  isHubspotAvailable
                    ? undefined
                    : t("chat.input.tools.crmUnavailable", { crm: "HubSpot" }),
                )}
              {isSeliarisConnected &&
                renderForcedToolItem(
                  "seliarisCompanies",
                  <img
                    src={seliarisIcon}
                    alt=""
                    aria-hidden
                    className={cn(iconClassName, "object-contain")}
                  />,
                  t("chat.input.tools.seliarisCompanies"),
                  isSeliarisAvailable
                    ? undefined
                    : t("chat.input.tools.crmUnavailable", { crm: "Seliaris" }),
                )}
            </>
          )}
          {isAdminMode && (
            <button
              className={cn(
                itemClassName,
                "bg-warning-foreground hover:bg-warning-foreground/80 active:bg-warning-foreground/80",
              )}
              onClick={() => {
                setToolsMenuOpen(false);
                setCompanyPickerOpen(true);
              }}
            >
              <Building2Icon className={iconClassName} />
              <span className="whitespace-nowrap">
                {t("chat.input.tools.addCompanyContext")}
              </span>
            </button>
          )}

          {/* Here rather than under the model list, where it used to sit: this
              is the menu that decides how the assistant works, and a switch is
              not a child a `listbox` admits — a screen reader enumerating the
              models had no reason to announce it at all. */}
          <div className={sectionLabelClassName}>
            {t("chat.input.tools.behaviour")}
          </div>
          <BehaviourSwitchRow
            variant={variant}
            label={t("chat.input.clarification.label")}
            hint={t("chat.input.clarification.hint")}
            checked={isClarificationEnabled}
            onCheckedChange={setIsClarificationEnabled}
          />
          {/* Under the questions, because the two answer the same worry in the
              same order: what the assistant does before it writes, then what it
              brings along while it writes. */}
          <BehaviourSwitchRow
            variant={variant}
            label={t("chat.input.autoTactics.label")}
            hint={t("chat.input.autoTactics.hint")}
            checked={isAutoTacticsEnabled}
            onCheckedChange={setIsAutoTacticsEnabled}
          />
        </div>
      );
    };

    return (
      <>
        <div className="relative" ref={composerRef}>
          {/* Above the composer, which sits at the bottom of the screen — and
              never at the same time as the slash menu, which a message made of
              nothing but an identifier can never trigger. */}
          {identifierCard.current && (
            <ChatCompanyIdentifierDropdown
              // Keyed on the identifier: a dropdown for another company starts
              // over, nothing of the previous one highlighted.
              key={identifierCard.current.key}
              ref={identifierDropdownRef}
              identifier={identifierCard.current.identifier}
              lookup={identifierCard.lookup}
              anchorRef={composerRef}
              onOpened={handleIdentifierCardOpened}
              onAskChat={askChatInstead}
              onRetry={identifierCard.retry}
              onClose={identifierCard.close}
            />
          )}
          {isSlashActive && slashTotalCount > 0 && (
            <SlashCommandAutocomplete
              ref={slashCommandRef}
              groups={slashGroups}
              totalCount={slashTotalCount}
              onSelect={handlePromptSelection}
              onDismiss={dismissSlashCommand}
            />
          )}
          {/* Above the composer rather than in a toast: the explanation is long,
              it has to stay on screen until the document is dealt with, and it
              sits right next to the card the user has to remove. */}
          {[...blockedByReason.entries()].map(([reason, files]) => (
            <AttachmentNotice
              key={reason}
              tone="blocked"
              title={t("chat.input.files.checks.blocked.title", {
                count: files.length,
              })}
              description={t(`chat.input.files.checks.blocked.${reason}`, {
                count: files.length,
                names: fileNames(files),
              })}
              actionLabel={t("chat.input.files.checks.remove", {
                count: files.length,
              })}
              onAction={() => removeFiles(files)}
            />
          ))}
          {/* Shown below the refusal, never instead of it: a document can be both
              a duplicate and too big, and the blocking half is what the user has
              to deal with first. */}
          {pendingDuplicates.length > 0 && (
            <AttachmentNotice
              tone="warning"
              title={t("chat.input.files.checks.duplicate.title", {
                count: pendingDuplicates.length,
              })}
              description={t("chat.input.files.checks.duplicate.description", {
                count: pendingDuplicates.length,
                names: fileNames(pendingDuplicates),
              })}
              actionLabel={t("chat.input.files.checks.remove", {
                count: pendingDuplicates.length,
              })}
              onAction={() => removeFiles(pendingDuplicates)}
              // Dismissing keeps the amber triangle on the card: the user chose
              // to send it, they did not decide it was wrong.
              dismissLabel={t("chat.input.files.checks.duplicate.keep", {
                count: pendingDuplicates.length,
              })}
              onDismiss={() =>
                setDismissedDuplicates(
                  (previous) => new Set([...previous, ...pendingDuplicates]),
                )
              }
            />
          )}
          {/* The composer stays visible while dictating on every device: the
              transcript streams into the textarea as the user speaks, so hiding
              it would remove the only feedback that dictation is working. Only
              the toolbar swaps to the recording controls, and the SpeechInput
              itself stays mounted to keep the session alive. */}
          <ChatComposerFrame
            // On a phone the composer floats over the conversation, as in the
            // Claude app: a rounder card lifted off the messages under it.
            className={cn(
              selectedPromptClassName,
              isMobile && "rounded-3xl shadow-lg"
            )}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
          {((lockedCompany && isAdminMode) || companyContext) && (
            <div className="flex flex-wrap gap-2 px-3 pt-3">
              {/* Never the same company twice. A chat docked on a company page
                  whose context is that same company rendered two chips under
                  one key, which React warns about and then stops telling apart,
                  remounting the avatars against each other. The removable chip
                  is the one kept: it says the same thing and carries the
                  action. */}
              {lockedCompany &&
                isAdminMode &&
                lockedCompany.id !== companyContext?.id &&
                renderCompanyChip(lockedCompany, false)}
              {companyContext && renderCompanyChip(companyContext, true)}
            </div>
          )}
          {uploadedFiles.length > 0 && (
            <div
              className={cn(
                "flex gap-2 p-3",
                isMobile ? "flex-wrap" : "overflow-x-auto",
              )}
            >
              {uploadedFiles.map((file, index) => {
                const verdict = attachmentChecks.verdicts.get(file);
                return (
                  <FileCard
                    key={index}
                    url={getFileObjectUrl(file)}
                    filename={file.name}
                    onRemove={() => removeFile(index)}
                    variant={isMobile ? "pill" : "card"}
                    status={verdict?.status ?? "checking"}
                    issueLabel={
                      verdict && "issue" in verdict
                        ? describeIssue(verdict.issue)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}
          <div className="relative">
            {forcedTool && (
              <button
                ref={forcedToolPrefixRef}
                type="button"
                onClick={() => setForcedTool(null)}
                className="group absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 text-base font-medium leading-6 text-majorelle duration-300 animate-in fade-in slide-in-from-left-2 zoom-in-95"
                aria-label={t(FORCED_TOOL_ACTIVE_LABEL_KEYS[forcedTool])}
              >
                {FORCED_TOOL_ICONS[forcedTool] ? (
                  <img
                    src={FORCED_TOOL_ICONS[forcedTool]}
                    alt=""
                    aria-hidden
                    className="object-contain size-4 group-hover:hidden touch:hidden"
                  />
                ) : (
                  <GlobeIcon className="size-4 group-hover:hidden touch:hidden" />
                )}
                <XIcon className="hidden size-4 group-hover:block touch:block" />
                <span>{t(FORCED_TOOL_ACTIVE_LABEL_KEYS[forcedTool])}</span>
              </button>
            )}
            {requestMode && RequestModeIcon && (
              <button
                ref={requestModePrefixRef}
                type="button"
                onClick={() => changeRequestMode(null)}
                className="group absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 text-base font-medium leading-6 text-majorelle duration-300 animate-in fade-in slide-in-from-left-2 zoom-in-95"
                aria-label={t("chat.input.modes.remove", {
                  mode: t(CHAT_REQUEST_MODE_LABEL_KEYS[requestMode]),
                })}
              >
                <RequestModeIcon className="size-4 group-hover:hidden touch:hidden" />
                <XIcon className="hidden size-4 group-hover:block touch:block" />
                <span>{t(CHAT_REQUEST_MODE_CHIP_KEYS[requestMode])}</span>
              </button>
            )}
            {isShowingExamples && <ChatPlaceholderExamples />}
          <TextareaAutosize
            ref={textareaRef}
            id="message"
            placeholder={placeholder}
            // The field carries no visible label, and the placeholder is
            // emptied while the examples own the slot: without this, a screen
            // reader reaches the composer and announces a nameless field.
            aria-label={t("chat.input.placeholder")}
            // On a small screen the field stops growing at a third of the
            // height and scrolls, so the thread above it stays in view.
            className="px-3 pt-3 pb-0 w-full text-base leading-6 bg-transparent border-0 shadow-none resize-none focus:outline-none compact:max-h-[30dvh]"
            style={{
              textIndent: forcedTool
                ? `${forcedToolPrefixWidth}px`
                : requestMode
                  ? `${requestModePrefixWidth}px`
                  : undefined,
            }}
            value={userInput}
            minRows={2}
            maxRows={15}
            onChange={(e) => setUserInput(e.target.value)}
            onPaste={(e) => {
              const items = Array.from(e.clipboardData.items);
              const imageFiles = items
                .filter((item) => item.type.startsWith("image/"))
                .map((item) => item.getAsFile())
                .filter((file): file is File => file !== null);
              if (imageFiles.length > 0) {
                e.preventDefault();
                processFilesUpload(imageFiles);
                return;
              }
              // Copying a whole line or paragraph carries the line breaks
              // that close it: pasted at the end of the field, they leave the
              // caret on an empty line under the text. Any other paste stays
              // native, undo included.
              const field = e.currentTarget;
              const pasted = e.clipboardData.getData("text/plain");
              const text = trimPastedText(pasted, field);
              if (text === pasted) return;
              e.preventDefault();
              if (!text) return;
              const { selectionStart, selectionEnd, value } = field;
              const caret = selectionStart + text.length;
              setUserInput(
                value.slice(0, selectionStart) + text + value.slice(selectionEnd),
              );
              requestAnimationFrame(() =>
                field.setSelectionRange(caret, caret),
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && identifierCard.current) {
                e.preventDefault();
                identifierCard.close();
                return;
              }
              // The field keeps the focus while the dropdown is open, so the
              // arrows and Enter are handed to it — and taken back the moment
              // it does not want them, which is every Enter a company is not
              // highlighted on.
              if (
                identifierCard.current &&
                identifierDropdownRef.current?.handleKeyDown(e)
              ) {
                return;
              }
              if (isSlashActive && slashTotalCount > 0) {
                if (e.key === "Escape") {
                  e.preventDefault();
                  dismissSlashCommand();
                  return;
                }
                if (
                  e.key === "Enter" ||
                  e.key === "Tab" ||
                  e.key === "ArrowUp" ||
                  e.key === "ArrowDown"
                ) {
                  slashCommandRef.current?.handleKeyDown(e);
                  return;
                }
              }
              if (e.key === "Escape" && selectedPrompt && !userInput) {
                e.preventDefault();
                removeSelectedPrompt();
                return;
              }
              // Backspace on an empty input removes the inline forced-tool prefix
              // (Dia-style chip deletion); with text present it deletes normally.
              if (e.key === "Backspace" && forcedTool && !userInput) {
                e.preventDefault();
                setForcedTool(null);
                return;
              }
              if (e.key === "Backspace" && requestMode && !userInput) {
                e.preventDefault();
                changeRequestMode(null);
                return;
              }
              if (!streamInProgress && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendNewMessage();
              }
            }}
            disabled={streamInProgress}
          />
          </div>
          <ChatComposerToolbar className="-mt-1">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept={FILES_UPLOAD_EXTENSIONS_ACCEPTED.map(
                (ext) => `.${ext}`
              ).join(",")}
              onChange={(e) => handleFileUpload(e)}
            />
            {isMobile && (
              <input
                ref={cameraInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileUpload(e)}
              />
            )}

            {/* Left group: enrich message. Unmounted while dictating so the
                recording controls get the full row; it carries no session
                state, unlike the SpeechInput below. */}
            {!isRecording && (
              <>
                <PromptsPopover
                  onSelectPrompt={handlePromptSelection}
                  onOpenLibrary={() => setDialogSelectPrompt(true)}
                  selectedPrompt={selectedPrompt}
                  onRemovePrompt={removeSelectedPrompt}
                  onViewPrompt={() => setDialogViewPrompt(true)}
                />

                {isMobile ? (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="shadow-none"
                      onClick={() => setToolsMenuOpen(true)}
                      aria-label={t("chat.input.tools.title")}
                    >
                      <PlusIcon className="size-4" />
                    </Button>
                    <MobileSheet
                      open={toolsMenuOpen}
                      onOpenChange={setToolsMenuOpen}
                      heightClass="h-auto"
                    >
                      <SheetTitle className="sr-only">
                        {t("chat.input.tools.title")}
                      </SheetTitle>
                      <SheetDescription className="sr-only">
                        {t("chat.input.tools.description")}
                      </SheetDescription>
                      <div className="px-3 pt-1 pb-4">
                        {renderToolsMenuItems("sheet")}
                      </div>
                    </MobileSheet>
                  </>
                ) : (
                  <Popover
                    open={companyPickerOpen}
                    onOpenChange={setCompanyPickerOpen}
                  >
                    <PopoverAnchor asChild>
                      <div className="inline-flex">
                        <Popover
                          open={toolsMenuOpen}
                          onOpenChange={setToolsMenuOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="shadow-none"
                            >
                              <PlusIcon className="size-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-fit p-1"
                            align="start"
                            side="top"
                            sideOffset={8}
                          >
                            {renderToolsMenuItems("popover")}
                          </PopoverContent>
                        </Popover>
                      </div>
                    </PopoverAnchor>
                    {isAdminMode && (
                      <ChatCompanyContextPicker
                        variant="popover"
                        open={companyPickerOpen}
                        onOpenChange={setCompanyPickerOpen}
                        onSelect={(company) => {
                          setCompanyContext(company);
                          setTimeout(() => textareaRef.current?.focus(), 0);
                        }}
                      />
                    )}
                  </Popover>
                )}
              </>
            )}

            {/* Right group: config + action */}
            <div
              className={cn(
                "flex items-center gap-2 ml-auto",
                isMobile && "min-w-0",
                // While dictating the bar owns the row instead of hugging right.
                isRecording && "flex-1 ml-0",
              )}
            >
              {/* What the last answered turn spent of the window. Admin-only:
                  it is a diagnostic, and the number it shows is an estimate. */}
              {!isRecording && contextUsage && (
                <ToggleAdminMode>
                  <ChatContextUsage info={contextUsage} />
                </ToggleAdminMode>
              )}
              {!streamInProgress && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Kept mounted (only hidden) while the recording controls
                        are shown, so the dictation session stays alive. */}
                    <div className={cn(isRecording && "hidden")}>
                      <SpeechInput
                        ref={speechInputRef}
                        variant="ghost"
                        size="icon"
                        onTranscript={handleTranscript}
                        language={speechLanguage}
                        onPermissionDenied={handlePermissionDenied}
                        onError={handleError}
                        onConnectionLost={handleConnectionLost}
                        onRecordingChange={handleRecordingChange}
                        onLiveTranscriptChange={setIsLiveTranscript}
                        onStreamChange={setRecordingStream}
                        showInlineVisualizer={false}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t("chat.input.speech.tooltip")}
                  </TooltipContent>
                </Tooltip>
              )}

              {isRecording ? (
                <VoiceRecordingBar
                  variant="inline"
                  fluid={isMobile}
                  isLiveTranscript={isLiveTranscript}
                  stream={recordingStream}
                  onCancel={handleCancelRecording}
                  onConfirm={handleConfirmRecording}
                />
              ) : isMobile ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setModelSheetOpen(true)}
                    className="gap-2 h-9 px-3 min-w-0 rounded-full shadow-none justify-between font-normal"
                    aria-label={t("chat.input.models.tooltip")}
                  >
                    <div className="flex items-center min-w-0">
                      {getSelectedModelLabel()}
                    </div>
                    <ChevronDownIcon className="size-4 opacity-50 shrink-0" />
                  </Button>
                  <MobileSheet
                    open={modelSheetOpen}
                    onOpenChange={setModelSheetOpen}
                    heightClass="h-[80dvh]"
                  >
                    <div className="px-5 pt-3 pb-3 shrink-0">
                      <SheetTitle className="text-2xl font-semibold tracking-tight">
                        {t("chat.input.models.select")}
                      </SheetTitle>
                      <SheetDescription className="sr-only">
                        {t("chat.input.models.tooltip")}
                      </SheetDescription>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-2 pb-4">
                      <button
                        type="button"
                        onClick={() => handleSelectModel("auto")}
                        className="w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left hover:bg-muted active:bg-muted transition-colors"
                      >
                        <SparklesIcon className="size-5 shrink-0 mt-0.5" />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium">
                            {t("chat.input.models.auto.label")}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("chat.input.models.auto.description")}
                          </span>
                        </div>
                        {llmSelection === "auto" && (
                          <CheckIcon className="size-5 shrink-0 text-primary" />
                        )}
                      </button>

                      {visibleLLMGroups.flatMap((group) => group.models).map((model) => (
                        <button
                          key={model.id}
                          type="button"
                          disabled={!model.available}
                          onClick={() =>
                            handleSelectModel(model.id as LLMSelection)
                          }
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors",
                            model.available
                              ? "hover:bg-muted active:bg-muted"
                              : "opacity-50 pointer-events-none",
                            model.adminOnly && "border-l-2 border-warning",
                          )}
                        >
                          {getModelLogo(model.groupId) ? (
                            <img
                              src={getModelLogo(model.groupId)}
                              alt={model.groupId}
                              className="size-5 shrink-0"
                            />
                          ) : (
                            <BrainCogIcon className="size-5 shrink-0" />
                          )}
                          <span className="flex-1 min-w-0 truncate">
                            {model.name}
                          </span>
                          {model.newUntil &&
                            new Date(model.newUntil) > new Date() && (
                              <Badge variant="secondary">
                                {t("chat.input.models.new")}
                              </Badge>
                            )}
                          {model.adminOnly && <LLMAdminOnlyBadge />}
                          {llmSelection === model.id && (
                            <CheckIcon className="size-5 shrink-0 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </MobileSheet>
                </>
              ) : (
                <Select
                  value={llmSelection}
                  onValueChange={(value) =>
                    setLLMSelection(value as LLMSelection)
                  }
                >
                  {/* Around the trigger alone: the content holds a tooltip of
                      its own, and nesting the two would have the model tooltip
                      arbitrate hovers inside the open dropdown. */}
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <SelectTrigger className="gap-2 w-auto rounded-full shadow-none">
                        <div className="flex items-center min-w-0">
                          {getSelectedModelLabel()}
                        </div>
                      </SelectTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t("chat.input.models.tooltip")}
                    </TooltipContent>
                  </Tooltip>
                  <SelectContent>
                    <SelectItem
                      value="auto"
                      className="py-3"
                      textValue={t("chat.input.models.auto.label")}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-2 items-center">
                          <SparklesIcon className="w-3 h-3" />
                          <span>{t("chat.input.models.auto.label")}</span>
                        </div>
                        <span className="text-xs leading-tight text-muted-foreground">
                          {t("chat.input.models.auto.description")}
                        </span>
                      </div>
                    </SelectItem>

                    {visibleLLMGroups.length > 0 && (
                      <>
                        {visibleLLMGroups.flatMap((group) => group.models).map((model) => (
                          <SelectItem
                            key={model.id}
                            value={model.id}
                            disabled={!model.available}
                            className={cn(
                              "flex items-center gap-2",
                              !model.available && "opacity-50",
                              model.adminOnly && "border-l-2 border-warning"
                            )}
                          >
                            <div className="flex gap-2 items-center">
                              {getModelLogo(model.groupId) ? (
                                <img
                                  src={getModelLogo(model.groupId)}
                                  alt={model.groupId}
                                  className="w-3 h-3"
                                />
                              ) : (
                                <BrainCogIcon className="w-3 h-3" />
                              )}
                              <span>{model.name}</span>
                              {model.newUntil &&
                                new Date(model.newUntil) > new Date() && (
                                  <Badge variant="secondary">
                                    {t("chat.input.models.new")}
                                  </Badge>
                                )}
                              {model.adminOnly && <LLMAdminOnlyBadge />}
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}

                  </SelectContent>
                </Select>
              )}

              {/* While dictating, the bar's confirm action stands in for send. */}
              {/* One button for send and stop, so the switch morphs in place —
                  width and radius through motion, colour through the CSS
                  transition — rather than swapping one element for another. */}
              {!isRecording && (
                <Button
                  asChild
                  variant={streamInProgress ? "destructive" : "default"}
                  size="icon"
                  className="relative w-auto overflow-hidden transition-colors duration-300"
                >
                  <motion.button
                    initial={getSendButtonShape(
                      mountedAsStop,
                      isMobile,
                      stopLabelWidth
                    )}
                    animate={getSendButtonShape(
                      streamInProgress,
                      isMobile,
                      stopLabelWidth
                    )}
                    transition={
                      shouldReduceMotion
                        ? { duration: 0 }
                        : { type: "spring", bounce: 0.15, duration: 0.35 }
                    }
                    onClick={
                      streamInProgress
                        ? triggerStopStream
                        : () => sendNewMessage()
                    }
                    disabled={
                      !streamInProgress &&
                      ((!userInput && !selectedPrompt) ||
                        blockedFiles.length > 0 ||
                        attachmentChecks.isChecking)
                    }
                    aria-label={
                      streamInProgress
                        ? isMobile
                          ? t("chat.input.stop")
                          : undefined
                        : t("chat.input.send")
                    }
                  >
                    <AnimatePresence
                      initial={mountedAsStop !== streamInProgress}
                      mode="popLayout"
                    >
                      <motion.span
                        key={streamInProgress ? "stop" : "send"}
                        ref={
                          streamInProgress && !isMobile
                            ? measureStopLabel
                            : undefined
                        }
                        className={cn(
                          "flex shrink-0 items-center gap-1.5",
                          streamInProgress && !isMobile && "px-3"
                        )}
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={
                          shouldReduceMotion ? { duration: 0 } : { duration: 0.2 }
                        }
                      >
                        {!streamInProgress ? (
                          <ArrowUpIcon className="size-4" />
                        ) : isMobile ? (
                          <CircleStopIcon className="size-4" />
                        ) : (
                          <>
                            {t("chat.input.stop")}
                            <CircleStopIcon className="size-3.5" />
                          </>
                        )}
                      </motion.span>
                    </AnimatePresence>
                  </motion.button>
                </Button>
              )}
            </div>
          </ChatComposerToolbar>
          </ChatComposerFrame>
        </div>

        {isMobile && isAdminMode && (
          <ChatCompanyContextPicker
            variant="sheet"
            open={companyPickerOpen}
            onOpenChange={setCompanyPickerOpen}
            onSelect={(company) => {
              setCompanyContext(company);
              setTimeout(() => textareaRef.current?.focus(), 0);
            }}
          />
        )}

        {dialogSelectPrompt && (
          <DialogSelectPrompt
            onSelect={(prompt) => handlePromptSelection(prompt)}
            onClose={() => setDialogSelectPrompt(false)}
          />
        )}
        {dialogViewPrompt && selectedPrompt && (
          <DialogViewPrompt
            prompt={selectedPrompt}
            onClose={() => setDialogViewPrompt(false)}
          />
        )}
        {dialogCreatePrompt && (
          <DialogCreatePrompt
            initialContent=""
            onClose={() => setDialogCreatePrompt(false)}
            onCreated={() => {
              // Notify other components about the new prompt
              notifyPromptsChanged();
              setDialogCreatePrompt(false);
            }}
          />
        )}
      </>
    );
  }
);

/**
 * The composer's verdict on an attachment, sitting just above the input.
 *
 * Not a toast and not a modal, on purpose. A toast disappears while the user is
 * still reading it, and a modal — which is what the duplicate warning used to be
 * — interrupts a message that is already written, so the only choice it can
 * offer is "send anyway" or "lose your place". Here the notice stays until the
 * document is dealt with, right next to the card carrying the same triangle.
 *
 * `tone` decides whether the send is blocked, not just the colour: a refusal is
 * red and offers only to remove the file, a warning is amber and can be waved
 * through.
 */
function AttachmentNotice({
  tone,
  title,
  description,
  actionLabel,
  onAction,
  dismissLabel,
  onDismiss,
}: {
  tone: "blocked" | "warning";
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
  dismissLabel?: string;
  onDismiss?: () => void;
}) {
  const isBlocked = tone === "blocked";

  return (
    <div
      // `status`, not `alert`: the notice is persistent rather than an
      // interruption, and one blocked send can render several of them at once —
      // `alert` made a screen reader cut across itself for each one.
      role="status"
      // Opaque like the composer frame it sits on top of: both float over the
      // scrolling thread, so a translucent tint alone left the conversation
      // showing through the text.
      className={cn(
        "overflow-hidden mb-2 w-full rounded-xl border bg-background duration-300 animate-in fade-in slide-in-from-bottom-1",
        isBlocked ? "border-destructive/30" : "border-amber-500/30",
      )}
    >
      <div
        className={cn(
          "flex gap-3 items-start p-4 text-sm",
          isBlocked ? "bg-destructive/5" : "bg-amber-500/5",
        )}
      >
        <AlertTriangleIcon
          className={cn(
            "mt-0.5 size-4 shrink-0",
            isBlocked ? "text-destructive" : "text-amber-600 dark:text-amber-500",
          )}
        />
        <div className="min-w-0">
          <p className="font-medium text-foreground">{title}</p>
          <p className="mt-1 break-words text-muted-foreground">{description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button type="button" size="sm" variant="outline" onClick={onAction}>
              {actionLabel}
            </Button>
            {dismissLabel && onDismiss && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onDismiss}
              >
                {dismissLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
